import React, { useEffect, useRef, useState } from 'react';
import { FileText, Folder, Paperclip, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { useI18n } from '../../hooks/useI18n';
import {
  createConversationInFolderApi,
  createFolderApi,
  deleteFolderApi,
  updateFolderApi,
  updateFolderFilesApi
} from '../../api/folders.api';
import { uploadMediaApi } from '../../api/media.api';
import { ACCEPTED_CHAT_FILE_ACCEPT, isAcceptedChatFile } from '../../constants/acceptedChatFiles';
import { notifyRecentActivityUpsert } from './recentActivityEvents';
import { classNames } from '../../utils/classNames';

const fileLabel = (file) => file?.name || file?.fileName || file?.title || file?.url || 'file';

const toPersistedFile = (item) => ({
  name: item.name,
  url: item.url,
  objectKey: item.objectKey,
  contentType: item.contentType
});

const fromFolderFiles = (items = []) =>
  items.map((item, index) => ({
    key: `${item.objectKey || item.url || fileLabel(item)}-${index}`,
    name: fileLabel(item),
    url: item.url,
    objectKey: item.objectKey,
    contentType: item.contentType
  }));

const filesSignature = (items = []) =>
  JSON.stringify(items.map((item) => `${item.objectKey || ''}|${item.url || ''}|${fileLabel(item)}`));

export const FolderSettingsModal = ({
  open,
  folder = null,
  onClose,
  onSaved,
  onDeleted
}) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [name, setName] = useState('');
  const [instructions, setInstructions] = useState('');
  const [color, setColor] = useState('#f97316');
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setName(folder?.name || '');
    setInstructions(folder?.instructions || '');
    setColor(folder?.color || '#f97316');
    setFiles(fromFolderFiles(folder?.files));
    setDragOver(false);
    setError('');
  }, [folder, open]);

  const addFiles = (fileList) => {
    if (!fileList?.length) return;
    const selected = Array.from(fileList);
    const accepted = selected.filter(isAcceptedChatFile);
    if (!accepted.length) {
      setError(t('folderFilesUnsupportedType'));
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setError(accepted.length < selected.length ? t('folderFilesUnsupportedType') : '');
    setFiles((prev) => [
      ...prev,
      ...accepted.map((file) => ({
        key: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
        name: file.name,
        contentType: file.type,
        file
      }))
    ]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (key) => {
    setFiles((prev) => prev.filter((item) => item.key !== key));
  };

  const resolveFiles = async (items) => {
    const resolved = [];
    for (const item of items) {
      if (item.file instanceof File) {
        const result = await uploadMediaApi(item.file);
        resolved.push({
          name: result.fileName || item.name,
          url: result.url,
          objectKey: result.objectKey,
          contentType: result.contentType || item.contentType || item.file.type
        });
      } else {
        resolved.push(toPersistedFile(item));
      }
    }
    return resolved;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t('folderName'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: trimmed,
        instructions,
        color
      };
      const hasPendingUploads = files.some((item) => item.file instanceof File);
      const resolvedFiles =
        hasPendingUploads || filesSignature(files) !== filesSignature(folder?.files || [])
          ? await resolveFiles(files)
          : null;

      let saved;
      if (folder?.id) {
        saved = await updateFolderApi(folder.id, payload);
        if (resolvedFiles) {
          saved = await updateFolderFilesApi(folder.id, resolvedFiles);
        }
        onSaved?.(saved);
        onClose?.();
        return;
      }

      saved = await createFolderApi({
        ...payload,
        filesJson: resolvedFiles ? JSON.stringify(resolvedFiles) : undefined
      });
      if (resolvedFiles?.length && !(saved.files || []).length) {
        saved = await updateFolderFilesApi(saved.id, resolvedFiles);
      }

      let conversation = null;
      try {
        conversation = await createConversationInFolderApi(saved.id);
        notifyRecentActivityUpsert({
          id: conversation.id,
          type: 'conversation',
          title: conversation.title || '',
          updatedAt: conversation.updatedAt || new Date().toISOString(),
          isPinned: Boolean(conversation.isPinned),
          isArchived: Boolean(conversation.isArchived),
          folderId: conversation.folderId || saved.id,
          isTemporary: Boolean(conversation.isTemporary)
        });
      } catch {
        conversation = null;
      }

      onSaved?.(saved);
      onClose?.();
      navigate(conversation?.id ? `/chat/${conversation.id}` : `/folders/${saved.id}`);
    } catch (err) {
      setError(err.message || t('folderCreateError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!folder?.id) return;
    setSaving(true);
    try {
      await deleteFolderApi(folder.id);
      onDeleted?.(folder.id);
      onClose?.();
    } catch (err) {
      setError(err.message || t('folderCreateError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => !saving && onClose?.()}
      closeDisabled={saving}
      title={folder ? t('folderSettings') : t('folderNew')}
      overlayClassName="!z-[90]"
      panelClassName="max-h-[90vh] max-w-lg overflow-y-auto rounded-2xl p-5"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <label className="block space-y-1.5">
          <span className="text-sm font-medium text-neutral-700 dark:text-neutral-200">{t('folderName')}</span>
          <div className="flex items-center gap-2 rounded-xl border border-neutral-200 bg-white px-2 py-2 dark:border-neutral-800 dark:bg-neutral-950">
            <input
              type="color"
              value={/^#[0-9a-fA-F]{6}$/.test(color) ? color : '#f97316'}
              onChange={(event) => setColor(event.target.value)}
              aria-label={t('folderColor')}
              className="h-8 w-8 shrink-0 cursor-pointer appearance-none rounded-lg border border-neutral-200 bg-white p-0 dark:border-neutral-800 [&::-moz-color-swatch]:rounded-md [&::-moz-color-swatch]:border-0 [&::-webkit-color-swatch]:rounded-md [&::-webkit-color-swatch]:border-none [&::-webkit-color-swatch-wrapper]:p-0"
            />
            <Folder size={16} style={{ color: color || '#f97316' }} className="shrink-0" />
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              autoFocus
              className="min-w-0 flex-1 bg-transparent text-sm outline-none"
            />
          </div>
        </label>

        <label className="block space-y-1.5">
          <span className="text-sm font-medium text-neutral-700 dark:text-neutral-200">{t('folderInstructions')}</span>
          <p className="text-xs text-neutral-500">{t('folderInstructionsHint')}</p>
          <textarea
            value={instructions}
            onChange={(event) => setInstructions(event.target.value)}
            rows={4}
            placeholder={t('folderInstructionsPlaceholder')}
            className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none dark:border-neutral-800 dark:bg-neutral-950"
          />
        </label>

        <div className="space-y-1.5">
          <span className="text-sm font-medium text-neutral-700 dark:text-neutral-200">{t('folderFiles')}</span>
          <p className="text-xs text-neutral-500">{t('folderSourcesBody')}</p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPTED_CHAT_FILE_ACCEPT}
            className="hidden"
            onChange={(event) => addFiles(event.target.files)}
          />
          <button
            type="button"
            disabled={saving}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(event) => {
              event.preventDefault();
              if (!saving) setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragOver(false);
              if (!saving) addFiles(event.dataTransfer.files);
            }}
            className={classNames(
              'flex w-full flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed px-3 py-5 text-center transition',
              dragOver
                ? 'border-accent bg-accent-soft/50 text-text'
                : 'border-neutral-200 bg-white text-neutral-500 hover:border-neutral-300 hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-950 dark:hover:bg-neutral-900'
            )}
          >
            <Paperclip size={16} className="pointer-events-none" />
            <span className="pointer-events-none text-sm font-medium">{t('folderAddFiles')}</span>
          </button>

          {files.length > 0 ? (
            <ul className="space-y-1.5 pt-1">
              {files.map((item) => (
                <li
                  key={item.key}
                  className="flex items-center gap-2 rounded-xl border border-neutral-200 px-3 py-2 dark:border-neutral-800"
                >
                  <FileText size={15} className="shrink-0 text-neutral-400" />
                  <span className="min-w-0 flex-1 truncate text-sm text-neutral-800 dark:text-neutral-200">
                    {item.name}
                  </span>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => removeFile(item.key)}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-neutral-400 transition hover:bg-neutral-200/70 hover:text-red-600"
                    aria-label={t('delete')}
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        {error ? <p className="text-sm text-red-500">{error}</p> : null}

        <div className="flex items-center justify-between gap-2 pt-1">
          {folder?.id ? (
            <Button
              type="button"
              variant="ghost"
              disabled={saving}
              onClick={handleDelete}
              className="!border !border-red-200 !text-red-600 dark:!border-red-900"
            >
              {t('folderDelete')}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button type="button" variant="ghost" disabled={saving} onClick={onClose}>
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={saving}>
              {saving && files.some((item) => item.file instanceof File) ? t('uploading') : t('save')}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};
